import React, { useRef, useCallback, useEffect, useState } from "react";
import { TimeoutSynchronizer } from "../helpers";

export interface UseFocusGroupOptions {
  extraRefs?: React.RefObject<HTMLElement | null>[];
  onFocusEnter?: () => void;
  onFocusLeave?: () => void;
  autoManage?: boolean;
}

export interface UseFocusGroupReturn {
  hasFocus: boolean;
  containerRef: React.RefObject<HTMLElement>;
  focusProps: {
    onFocus: (event: React.FocusEvent) => void;
    onBlur: (event: React.FocusEvent) => void;
  };
  setFocused: (focused: boolean) => void;
}

/**
 * A global controller that allows to delay actions but if multiple useFocusGroup activate at the same time (for example,
 * one group activates while other deactivates), both can send delayed state changes that triggers at the same time.
 * That allow us to wait for all DOM events to happen for each one, while triggering only one re-render due to both state changes
 */
const globalSynchronizer = new TimeoutSynchronizer(100);

/**
 * Custom hook for managing focus state within a group of elements.
 *
 * Tracks focus state for a container and optionally related elements (like portaled calendars).
 * Focus is maintained when:
 * - User focuses on elements within the container
 * - User focuses on elements inside selected elements outside the container
 *
 * @param options Configuration options
 * @param options.extraRefs Array of refs of elements that, upon interaction, should still keep focus to the main element
 * @param options.onFocusEnter Callback fired when focus enters the group
 * @param options.onFocusLeave Callback fired when focus leaves the group
 * @param options.autoManage Whether to automatically manage focus state (default: true)
 *
 * @returns Object with focus state, container ref, focus props, and manual focus control
 *
 * @example
 * ```tsx
 * const { hasFocus, containerRef, focusProps } = useFocusGroup({
 *   extraRefs: [overlayRef1, overlayRef2],
 *   onFocusEnter: () => console.log("Focus entered"),
 *   onFocusLeave: () => console.log("Focus left")
 * });
 *
 * return (
 *   <div ref={containerRef} {...focusProps}>
 *     {hasFocus && <KeyboardInput />}
 *   </div>
 * );
 * ```
 */
export const useFocusGroup = (options: UseFocusGroupOptions = {}): UseFocusGroupReturn => {
  const { extraRefs = [], onFocusEnter, onFocusLeave, autoManage = true } = options;

  const [hasFocus, setHasFocus] = useState(false);
  const containerRef = useRef<HTMLElement>(null);
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  const isOverlayVisible = useCallback((overlay: HTMLElement) => {
    return overlay.offsetParent !== null && getComputedStyle(overlay).display !== "none";
  }, []);

  const isElementWithinGroup = useCallback(
    (element: Element | null) => {
      const container = containerRef.current;
      if (!container) return false;

      // Handle null element (gaps in calendar) - check if any overlay is visible
      if (!element) {
        return extraRefs.some((ref) => {
          const overlay = ref.current;
          return overlay && isOverlayVisible(overlay);
        });
      }

      // Check main container
      if (container.contains(element)) return true;

      // Check extra refs (overlays)
      return extraRefs.some((ref) => {
        if (!ref.current) return false;

        const contains = ref.current.contains(element);

        const isBodyOrHtml = element.tagName === "BODY" || element.tagName === "HTML";
        const overlayVisible = isOverlayVisible(ref.current);

        return contains || (isBodyOrHtml && overlayVisible);
      });
    },
    [extraRefs, isOverlayVisible],
  );

  const updateFocusState = useCallback(
    (focused: boolean) => {
      if (hasFocus !== focused) {
        globalSynchronizer.setSynchronizedTimeout(() => setHasFocus(focused));

        if (focused) {
          onFocusEnter?.();
        } else {
          onFocusLeave?.();
        }
      }
    },
    [hasFocus, onFocusEnter, onFocusLeave],
  );

  const handleFocus = useCallback(() => {
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }

    if (autoManage) {
      updateFocusState(true);
    }
  }, [autoManage, updateFocusState]);

  const handleBlur = useCallback(
    (event: React.FocusEvent) => {
      if (!autoManage) return;

      const relatedTarget = event.relatedTarget as Element | null;

      // Focus is moving within the group, maintain focus
      if (relatedTarget && isElementWithinGroup(relatedTarget)) {
        return;
      }

      focusTimeoutRef.current = setTimeout(() => {
        const currentlyWithinGroup = isElementWithinGroup(document.activeElement);
        if (!currentlyWithinGroup) {
          updateFocusState(false);
        }
      }, 50); // Reduced timeout for better responsiveness
    },
    [autoManage, isElementWithinGroup, updateFocusState],
  );

  const setFocused = useCallback(
    (focused: boolean) => {
      updateFocusState(focused);
    },
    [updateFocusState],
  );

  return {
    hasFocus,
    containerRef,
    focusProps: {
      onFocus: handleFocus,
      onBlur: handleBlur,
    },
    setFocused,
  };
};
